import styles from './RegulationsContent.module.scss';

import termsIcon from '@/assets/icons/terms-icon.svg';
import donationIcon from '@/assets/icons/donation-icon.svg';
import supportIcon from '@/assets/icons/support-icon.svg';
import faqIcon from '@/assets/icons/faq-icon.svg';
import { useGetApi } from '@/hooks/UseApiCalls';
import { OverheadFeeScheduleDtoOut } from '@/models/out/OverheadFeeScheduleDtoOut';
import { OVERHEAD_FEES_URL, overheadDocumentHref } from '@/helpers/overheadFees';

/**
 * The organization's regulations in full - joining conditions, donation and support procedures,
 * classification limits and overheads.
 *
 * Written for the open-fund request's third step, where an applicant reads them before signing.
 * They live here rather than in that step because the portal's entry gate (HAT-1256) asks every
 * coordinator to confirm the same regulations and has to be able to show them the same text: the
 * gate's dialog links to RegulationsPage, which renders exactly this.
 */

const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
  <div className={styles.termsSectionHeader}>
    <div className={styles.termsSectionHeaderIcon}>
      <img src={icon} alt="" aria-hidden="true" />
    </div>
    <span className={styles.termsSectionHeaderTitle}>{title}</span>
  </div>
);

export const TermsSection = () => (
  <div className={styles.termsCard}>
    <SectionHeader icon={termsIcon} title="תנאים להצטרפות" />
    <div className={styles.termsCardBody}>
      <p className={styles.termsText}>
        לקראת פתיחת קרן ופעילות במסגרת העמותה ריכזנו עבורכם את עיקרי ההנחיות לצורך פעילות בעמותה. ההנחיות המפורטות הינן בהתאם לנהלי העמותה וכפי שיתעדכנו מעת
        לעת. מובהר כי בכל מקרה של סתירה או חוסר התאמה בין האמור במסמך זה לבין נהלי העמותה, יגבר האמור בנהלי העמותה.
      </p>

      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>הקמת קרן:</p>
        <ol className={styles.termsNumberedList}>
          <li>במסגרת פעילותה ובהתאם למטרותיה, העמותה פותחת קרנות צדקה ייעודיות לתמיכה בנזקקים ופעילות קהילתית ואחרת בהתאם למטרות העמותה.</li>
          <li>
            העמותה מקימה שני סוגים עיקריים של קרנות:
            <div className={styles.termsSubItems}>
              <span>א. &quot;קרן רווחה&quot; – לסיוע צדקה לנתמך ספציפי ומוגדר מראש.</span>
              <span>ב. &quot;קרן קהילתית&quot; – סיוע, תמיכה ופעילות קהילתית בהתאם למטרות העמותה.</span>
            </div>
          </li>
          <li>
            הקמת הקרן מתבצעת בשיתוף &quot;רכז הקרן&quot; שמרכז את פעילות הקרן והוא הגורם האחראי מטעם העמותה על גיוס תרומות לקרן ועל פעילותה השוטפת של הקרן בהתאם
            למטרות העמותה ונהליה.
          </li>
          <li>מובהר כי הקמת הקרן וכן שמה המוגדר של הקרן יאושר בהתאם להמלצת רכז הקרן לאחר בדיקה של העמותה בהתאם לנהליה.</li>
          <li>במקרה של קרן שפעילותה כוללת שיתוף פעולה עם ארגון או מלכ&quot;ר אחר תבחן העמותה את הצורך בחתימה על הסכם שיתוף פעולה עם הארגון האחר.</li>
        </ol>
        <div className={styles.termsDivider} />
      </div>

      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>חתימת רכז הקרן על טפסי הבקשה להקמת קרן מהווה הצהרה לפיה, בין היתר:</p>
        <ol className={styles.termsNumberedList}>
          <li>הוא מודע לכך שכל פעילות המתבצעת בקרן יעשה אך ורק בכפוף לנהלי העמותה ועמידה פרטנית בכל התנאים והכללים בהנחיות אלו ובנהלי העמותה.</li>
          <li>הרכז מודע כי העמותה תקצה מהתרומות סכומים בהתאם למפורט להלן בפרק העוסק בתקורות.</li>
          <li>הרכז מודע כי תמיכות יועברו בהתאם לנוהל תמיכות וההנחיות לתמיכה המופרטות להלן.</li>
          <li>
            הרכז מודע ויבהיר לתורמי הקרן כי העמותה רשאית לקיים פיקוח ובדיקות מדגמיות על פעילות הקרן בהתאם לנהלים ולהנחיות וכן על התאמת חשבונות הבנקים לשמות
            הנתמכים ולפעול על פי שיקול דעתה הבלעדי, במקרים בהם עלה חשד להתנהלות לא תקינה ו/או במקרה בו התברר כי כספים הועברו לחשבונות שאינם על שם נתמכים שאושרו
            על ידה.
          </li>
          <li>כל פנייה לתורמים לצורך גיוס תרומות עבור העמותה תתבצע בתיאום מלא עם העמותה ובהתאם לנוהל גיוס תרומות של העמותה.</li>
          <li>
            הרכז יידע את התורמים לכך שתרומתם כפופה לנהלי העמותה והם מודעים לכך שהשימוש בתרומות יהיה אך ורק בהתאם לנהלי העמותה לרבות לעניין קרנות לא פעילות.
          </li>
        </ol>
        <div className={styles.termsDivider} />
      </div>

      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>תנאים נוספים:</p>
        <ol className={styles.termsNumberedList}>
          <li>מובהר כי בכל מקרה של פעילות שאינה תואמת את מטרות העמותה, או דיווחים שגויים, העמותה רשאית לסגור את הקרן בהתאם להוראות המפורטות בנהלי העמותה.</li>
          <li>
            הכספים העודפים בקרן שנסגרה, יועברו לפעילותה השוטפת של העמותה לקידום מטרות קרובות ככל הניתן למטרה לשמה הוקמה הקרן, וזאת בהתאם לשיקול דעתו של נציג
            העמותה, ללא צורך במתן התראה על כך לרבות לא לרכז הקרן.
          </li>
        </ol>
      </div>
    </div>
  </div>
);

export const DonationSection = () => (
  <div className={styles.termsCard}>
    <SectionHeader icon={donationIcon} title="נהלי תרומה לעמותה" />
    <div className={styles.termsCardBody}>
      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>נהלי תרומה:</p>
        <ol className={styles.termsNumberedList}>
          <li>
            תרומות במזומן יתקבלו אך ורק בהתאם למגבלות הקבועות בחוק לצמצום השימוש במזומן, תשע&quot;ח-2018. בהתאם לכך לא תתקבל תרומה במזומן, מתורם יחיד, בסכום
            העולה על 6,000 ₪ או בכל סכום אחר שיועדכן בהתאם לדין.
          </li>
          <li>
            תרומה של אדם (לרבות תאגיד בשליטתו) לקרן ממנה נהנה קרובו כהגדרתו בסעיף 76(ד) לפקודת מס הכנסה, תוגבל לשיעור של עד 10% לכל קרוב, מסך התרומות הכלליות
            באותה קרן, או מהסכום המועבר לאותו קרוב, לפי הגבוה מביניהם, בכל שנת כספים.
          </li>
          <li>
            העמותה תנפיק קבלה על שמו של תורם בגין כל תרומה בכסף או בשווה כסף. הקבלה תועבר לתורם לרבות באמצעות כתובת דואר אלקטרוני שסופקה על ידו ו/או באמצעות רכז
            הקרן ו/או בכל דרך סבירה נוספת לבקשת התורם.
          </li>
          <li>
            ככל שהסכום שגויס בקרן כלשהי יעלה על הצורך בתמיכה בהתאם למטרות אותה קרן, כספי התרומות העודפים יועברו לפעילות העמותה לשם קידום מטרות דומות ככל הניתן,
            בהתאם לשיקול דעת העמותה ובתיאום עם רכז הקרן ככל שניתן.
          </li>
          <li>לאחר אישור פתיחת הקרן ישלח קישור איתו ניתן לגייס תרומות ישירות לקרן, כמו כן ניתן לפתוח ממשק אישי בנדרים פלוס לניהול התרומות בקרן.</li>
        </ol>
      </div>
      <div className={styles.termsDivider} />

      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>תרומות באמצעות עמותת charity center:</p>
        <ol className={styles.termsNumberedList}>
          <li>
            ניתן לגייס תרומות באמצעות עמותת charity center הרשומה בארה&quot;ב ומוכרות לצרכי מס בארה&quot;ב (העמותה הינה ישות נפרדת ופועלת בשיתוף פעולה עם מרכז
            הצדקה)
          </li>
          <li>התרומות מסונכרנות מיידית בעמותה בישראל בהתאם לשער היציג (ישנה עמלת המרת מט&quot;ח של 1%)</li>
          <li>ניתן לבקש מהמשרד קישור אישי לתרומות באשראי, ופרטים לצורך תרומות באמצעים אחרים, אין עלות העברה בין העמותות.</li>
        </ol>
      </div>
      <div className={styles.termsDivider} />

      <div className={styles.termsSubSection}>
        <p className={styles.termsTextBold}>פרסום וגיוס תרומות:</p>
        <ol className={styles.termsNumberedList}>
          <li>
            בכל פניה לגיוס תרומות יובהר ויודגש לתורמים כי התרומות הינן בהתאם לנהלי העמותה והתורמים יהיו מודעים לכך שהשימוש בתרומות יהיה אך ורק בהתאם לנהלי
            העמותה לרבות לעניין קרן לא פעילה.
          </li>
          <li>
            פנייה לתורמים, תתבצע בכפוף לעמידה בכללי הוראות כל דין ובייחוד חוק התקשורת (בזק ושידורים) תשמ&quot;ב-1982 (חוק הספאם), בתיאום מלא עם הנהלת העמותה
            ובהתאם לנהלי העמותה.
          </li>
          <li>במקרה של פעולות לגיוס תרומות בשיתוף פעולה עם ארגון או עמותה אחרת, הפרסום יבחן ויאושר על ידי נציג העמותה מראש.</li>
          <li>
            ניתן לפנות בבקשה לעמותה להקצות סכומים מתוך כספי התרומות, לצורך מימון הוצאות הגיוס של קרן שלא יעלה על 35% מסך התרומות שנתרמו לאותה קרן או 50,000 ₪,
            לפי הגבוה מביניהם.
          </li>
        </ol>
      </div>
    </div>
  </div>
);

export const SupportSection = () => {
  const allocationRows = [
    {
      type: 'משפחה נזקקת',
      maxAmount: '4,000 ₪ או 1,000 ₪ לנפש',
      holidays: '8,000 ₪ או 2,000 ₪ לנפש',
      summer: '6,000 ₪ או 1,500 ₪ לנפש',
      notes: 'מימון 100% עבור הוצאות טיפולי שיניים',
    },
    { type: 'משפחה מתקשה', maxAmount: '2,000 ₪', holidays: '3,000 ₪', summer: '2,500 ₪', notes: 'מימון 75% מסכום ההוצאה עבור הוצאות טיפולי שיניים' },
    {
      type: 'משפחה חד-הורית',
      maxAmount: '4,000 ₪ או 1,500 ₪ לנפש',
      holidays: '8,000 ₪ או 3,000 ₪ לנפש',
      summer: '6,000 ₪ או 1,500 ₪ לנפש',
      notes: 'מימון 100% עבור הוצאות טיפולי שיניים',
    },
    { type: 'בעל מוגבלות', maxAmount: '6,000 ₪', holidays: '--', summer: '--', notes: 'סיוע בביצוע התאמת נגישות בסכום שנתי של עד 40,000 ₪' },
    { type: 'מקרה רפואי', maxAmount: '5,000 ₪', holidays: '--', summer: '--', notes: 'סיוע במימון הוצאות רפואיות בסכום שנתי של עד 50,000 ₪' },
  ];

  return (
    <div className={styles.termsCard}>
      <SectionHeader icon={supportIcon} title="נהלי תמיכה והגבלות" />
      <div className={styles.termsCardBody}>
        <p className={styles.termsText}>העמותה תעביר תמיכות בהתאם לנהליה על פי המלצות ובקשות של רכז הקרן.</p>
        <p className={styles.termsTextBold}>התמיכות למשפחות הינם בהתאם לקטגוריות הבאות:</p>
        <ol className={styles.termsNumberedList}>
          <li>
            &quot;משפחה נזקקת&quot; – בכפוף לאישור על הנחה בארנונה, או אישור עובד סוציאלי, או אישור מעמותת רווחה, או אישור זכאות להבטחת הכנסה או השלמת הכנסה.
          </li>
          <li>&quot;משפחה חד הורית&quot; – בכפוף להצגת ספח תעודת הזהות, או אסמכתא מבית דין רבני או בית משפט, או אישור של עובד סוציאלי.</li>
          <li>&quot;משפחה מתקשה&quot; – בהתאם להצהרה של רכז הקרן הכוללת את היות המשפחה מעוטת יכולת תוך פירוט נתונים לביסוס זאת.</li>
          <li>&quot;בעל מוגבלות&quot; – בכפוף לאישור מביטוח לאומי על נכות בשיעור של 60%, או אישור של עובד סוציאלי.</li>
          <li>&quot;מקרה רפואי&quot; - בכפוף לאישור רפואי או אישור של עובד סוציאלי, על התמודדות עם מחלה קשה, מחלה כרונית, טיפולי פוריות.</li>
        </ol>

        <div className={styles.termsTableWrap}>
          <table className={styles.termsTable}>
            <thead>
              <tr>
                <th>סוג סיווג</th>
                <th>סכום מקסימלי</th>
                <th>חודשי חגים</th>
                <th>חודשי החופש</th>
                <th>הערות נוספות</th>
              </tr>
            </thead>
            <tbody>
              {allocationRows.map((row, i) => (
                <tr key={i}>
                  <td>{row.type}</td>
                  <td>
                    {row.maxAmount}
                    {row.type === 'משפחה נזקקת' && <div style={{ fontSize: '0.85em', opacity: 0.7 }}>(לפי הגבוה מביניהם)</div>}
                  </td>
                  <td>{row.holidays}</td>
                  <td>{row.summer}</td>
                  <td>{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const CommissionCostsSection = () => {
  /*
   * HAT-1231: the overhead rates moved to the server, where the downloadable overheads document is
   * generated from the same rows - so this table, the settings screen and that document cannot
   * drift apart. The endpoint is anonymous precisely because this page is public.
   *
   * If it cannot be read, the section says where the rates can be found rather than printing a
   * silently empty table into a document somebody is about to sign.
   */
  const { data: schedule, isLoading: isScheduleLoading } = useGetApi<OverheadFeeScheduleDtoOut>(OVERHEAD_FEES_URL);
  const rows = schedule?.rows ?? [];

  return (
    <div className={styles.termsCard}>
      <SectionHeader icon={faqIcon} title="תקורות" />
      <div className={styles.termsCardBody}>
        <p className={styles.termsText}>
          בנוסף, העמותה תקצה מתוך כספי התרומות סכומים עבור תחזוקת הקרנות ותפעולה השוטף והאדמיניסטרטיבי של העמותה, התקורות הינם בהתאם לסוג התרומה וערוץ
          הגבייה כמפורט להלן:
        </p>
        {isScheduleLoading ? (
          <p className={styles.termsText}>טוען…</p>
        ) : rows.length === 0 ? (
          <p className={styles.termsText}>
            טבלת התקורות המלאה נמצאת במסמך{' '}
            <a href={overheadDocumentHref} target="_blank" rel="noreferrer">
              «תקורות מרכז הצדקה»
            </a>
            .
          </p>
        ) : (
          <div className={styles.termsTableWrap}>
            <table className={styles.termsTable}>
              <thead>
                <tr>
                  <th>פעולה</th>
                  <th>תקורה</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.action}</td>
                    <td>{row.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/** The intro sentence above the cards. Shared so both screens introduce the regulations alike. */
export const RegulationsIntro = () => (
  <p className={styles.termsIntro}>
    לפני פתיחת קרן קהילתית או רווחה <strong>יש לעיין בכל הנהלים וההנחיות</strong> – נהלי תרומה, תמיכה, סיווגים ותנאי הצטרפות.
  </p>
);

/** Every section, in reading order. */
const RegulationsContent = () => (
  <>
    <TermsSection />
    <DonationSection />
    <SupportSection />
    <CommissionCostsSection />
  </>
);

export default RegulationsContent;
